import { ContractId, TokenId } from "@hashgraph/sdk";
import { ethers } from "ethers";
import { formatEther, formatUnits } from "ethers/lib/utils.js";
import { hederaAppConfig } from "../../../constants/hedera.contants";
import { ContractFunctionParameterBuilder } from "./contractFunctionParameterBuilder";

const getProvider = (): ethers.providers.Web3Provider | null => {
  if (typeof window !== "undefined" && (window as any).ethereum) {
    return new ethers.providers.Web3Provider((window as any).ethereum);
  }

  return null;
};

async function associateToken(tokenId: TokenId) {
  // send the transaction
  // convert tokenId to contract id
  const hash = await executeContractFunction(
    ContractId.fromString(tokenId.toString()),
    "associate",
    new ContractFunctionParameterBuilder(),
    hederaAppConfig.constants.METAMASK_GAS_LIMIT_ASSOCIATE
  );

  return hash;
}

async function approveToken(
  tokenId: TokenId,
  spender: ContractId,
  amount: string
) {
  const hash = await executeContractFunction(
    ContractId.fromString(tokenId.toString()),
    "approve",
    new ContractFunctionParameterBuilder()
      .addParam({
        type: "address",
        name: "spender",
        value: spender.toSolidityAddress(),
      })
      .addParam({
        type: "uint256",
        name: "amount",
        value: ethers.utils.parseUnits(amount, 8).toString(),
      }),
    hederaAppConfig.constants.METAMASK_GAS_LIMIT_ASSOCIATE
  );

  return hash;
}

async function stakeHbar(stakeAmount: string, contractId: ContractId) {
  const hash = await executeContractFunctionPayable(
    contractId, // ContractId.fromString("0.0.1412503"),
    "stake",
    new ContractFunctionParameterBuilder(),
    hederaAppConfig.constants.METAMASK_GAS_LIMIT_ASSOCIATE,
    stakeAmount
  );

  return hash;
}

async function unstakeHbar(unstakeAmount: string, contractId: ContractId) {
  const hash = await executeContractFunction(
    contractId, // ContractId.fromString("0.0.1412503"),
    "unStake",
    new ContractFunctionParameterBuilder().addParam({
      type: "uint256",
      name: "amount",
      value: ethers.utils.parseUnits(unstakeAmount, 8),
    }),
    hederaAppConfig.constants.METAMASK_GAS_LIMIT_ASSOCIATE
  );

  return hash;
}

async function withdrawHbar(withdrawIndex: number, contractId: ContractId) {
  const hash = await executeContractFunction(
    contractId, // ContractId.fromString("0.0.1412465"),
    "withdraw",
    new ContractFunctionParameterBuilder().addParam({
      type: "uint256",
      name: "index",
      value: ethers.utils.parseUnits(withdrawIndex.toString(), 0),
    }),
    hederaAppConfig.constants.METAMASK_GAS_LIMIT_ASSOCIATE
  );

  return hash;
}

async function fetchBalance(address: string, tokenIdEvm: string) {
  const provider = getProvider();

  if (!provider) {
    throw new Error("No provider found");
  }

  const ERC20_ABI = [
    "function balanceOf(address owner) view returns (uint256)",
    "function decimals() view returns (uint8)",
  ];

  const hbarBalanceRaw = await provider.getBalance(address);
  const token = new ethers.Contract(tokenIdEvm, ERC20_ABI, provider);
  const rawBalance = await token.balanceOf(address);
  const decimals = await token.decimals();

  return {
    hbar: formatEther(hbarBalanceRaw),
    hbarx: formatUnits(rawBalance, decimals),
  };
}

/**
 * Purpose: build contract execute transaction and send to hashconnect for signing and execution
 * @returns Promise<TransactionId | null>
 * @throws Error if provider is not found
 */
async function executeContractFunctionPayable(
  contractId: ContractId,
  functionName: string,
  functionParameters: ContractFunctionParameterBuilder,
  gasLimit: number,
  value: string
) {
  const provider = getProvider();

  if (!provider) {
    throw new Error("No provider found");
  }

  const signer = await provider.getSigner();
  const abi = [
    `function ${functionName}(${functionParameters.buildAbiFunctionParams()}) payable`,
  ];

  // create contract instance for the contract id
  // to call the function, use contract[functionName](...functionParameters, ethersOverrides)
  const contract = new ethers.Contract(
    `0x${contractId.toSolidityAddress()}`,
    abi,
    signer
  );
  try {
    const txResult = await contract[functionName](
      ...functionParameters.buildEthersParams(),
      {
        gasLimit: gasLimit === -1 ? undefined : gasLimit,
        value: ethers.utils.parseEther(value.toString()),
      }
    );
    return txResult.hash;
  } catch (error: any) {
    console.warn(error.message ? error.message : error);
    return null;
  }
}

async function executeContractFunction(
  contractId: ContractId,
  functionName: string,
  functionParameters: ContractFunctionParameterBuilder,
  gasLimit: number
) {
  const provider = getProvider();

  if (!provider) {
    throw new Error("No provider found");
  }

  const signer = await provider.getSigner();
  const abi = [
    `function ${functionName}(${functionParameters.buildAbiFunctionParams()})`,
  ];

  // create contract instance for the contract id
  // to call the function, use contract[functionName](...functionParameters, ethersOverrides)
  const contract = new ethers.Contract(
    `0x${contractId.toSolidityAddress()}`,
    abi,
    signer
  );
  try {
    const txResult = await contract[functionName](
      ...functionParameters.buildEthersParams(),
      {
        gasLimit: gasLimit === -1 ? undefined : gasLimit,
      }
    );
    return txResult.hash;
  } catch (error: any) {
    console.warn(error.message ? error.message : error);
    return null;
  }
}

async function convertEvmAddressToHederaAddress(address: string) {
  const url =
    process.env.NEXT_PUBLIC_NETWORK === "testnet"
      ? "https://testnet.mirrornode.hedera.com/api/v1/accounts/"
      : "https://mainnet-public.mirrornode.hedera.com/api/v1/accounts/";

  const response = await fetch(url + address);
  const data = await response.json();

  return data.account;
}

export {
  associateToken as associateHederaEvmToken,
  approveToken as approveHederaEvmToken,
  stakeHbar as stakeHederaEvm,
  unstakeHbar as unstakeHederaEvm,
  withdrawHbar as withdrawHederaEvm,
  fetchBalance as fetchHederaEvmBalance,
  convertEvmAddressToHederaAddress,
  getProvider,
};
